import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Helper function to detect system theme preference
const getSystemTheme = () => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        // Priority 1: Check if user has manually set a preference
        const savedTheme = localStorage.getItem('myedi-theme');
        
        // Priority 2: Check if user preference is set to 'auto' or doesn't exist
        const themePreference = localStorage.getItem('myedi-theme-preference');
        
        if (savedTheme && themePreference === 'manual') {
            // User has explicitly chosen a theme
            return savedTheme;
        }
        
        // Priority 3: Use system preference (default behavior)
        return getSystemTheme();
    });

    const [isAutoMode, setIsAutoMode] = useState(() => {
        const themePreference = localStorage.getItem('myedi-theme-preference');
        return themePreference !== 'manual';
    });

    // Listen for system theme changes
    useEffect(() => {
        if (!isAutoMode) return; // Don't listen if user has manual preference

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        const handleChange = (e) => {
            const newTheme = e.matches ? 'dark' : 'light';
            setTheme(newTheme);
            localStorage.setItem('myedi-theme', newTheme);
        };

        // Modern browsers
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        } 
        // Fallback for older browsers
        else if (mediaQuery.addListener) {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
    }, [isAutoMode]);

    useEffect(() => {
        // Apply theme to body element
        document.body.setAttribute('data-theme', theme);
        // Save current theme
        localStorage.setItem('myedi-theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        // When user manually toggles, switch to manual mode
        setIsAutoMode(false);
        localStorage.setItem('myedi-theme-preference', 'manual');
        
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
    };

    const setAutoMode = () => {
        // Switch back to automatic mode based on system preference
        setIsAutoMode(true);
        localStorage.setItem('myedi-theme-preference', 'auto');
        
        const systemTheme = getSystemTheme();
        setTheme(systemTheme);
    };

    const value = {
        theme,
        toggleTheme,
        setAutoMode,
        isAutoMode,
        isDark: theme === 'dark'
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};